import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/shared/ThemeContext'
import type { Message } from '../../shared/types/src';
import apiService from '../services/shared/apiService';
import { localDbService } from '../services/shared';

interface ChatReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  contentMode: 'G' | 'PG';
}

export const ChatReviewModal: React.FC<ChatReviewModalProps> = ({
  isOpen,
  onClose,
  userId,
  contentMode
}) => {
  const { themeClasses } = useTheme();
  const [chatHistory, setChatHistory] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChatHistory = React.useCallback(async () => {
    // Only load if modal is actually open
    if (!isOpen) {
      console.log('ChatReviewModal: Skipping history load - modal not open');
      return;
    }
    
    console.log('ChatReviewModal: Loading chat history for user:', userId);
    setIsLoading(true);
    setError(null);
    try {
      // HIPAA COMPLIANT: Use local storage only for chat history
      // This ensures sensitive clinical data never leaves the device
      const historyData = await localDbService.getChatHistory(userId);
      
      // Merge Claude and OpenAI messages for display
      const allMessages: Message[] = [
        ...(historyData.claude || []),
        ...(historyData.openai || [])
      ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      setChatHistory(allMessages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chat history from local storage');
      console.error('Error loading chat history from local storage:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadChatHistory();
    } else {
      // Clear chat history when modal is closed to prevent memory leaks
      setChatHistory([]);
      setError(null);
    }
  }, [isOpen, loadChatHistory]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${themeClasses.cardBg} w-full max-w-5xl max-h-[80vh] rounded-lg shadow-xl p-6 m-4 overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-semibold ${themeClasses.primaryText}`}>
            Chat History Review ({contentMode} Mode)
          </h2>
          <button
            onClick={onClose}
            className={`${themeClasses.buttonSecondary} p-2 rounded-lg`}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto">
          {isLoading ? (
            <div className={`flex justify-center items-center h-32 ${themeClasses.mutedText}`}>
              Loading chat history...
            </div>
          ) : error ? (
            <div className={`text-red-500 text-center p-4`}>
              {error}
            </div>
          ) : chatHistory.length === 0 ? (
            <div className={`text-center p-4 ${themeClasses.mutedText}`}>
              No chat history found.
            </div>
          ) : (
            <div className="space-y-4">
              {chatHistory.map((message, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${themeClasses.border}`}
                >
                  <div className={`font-medium mb-1 ${themeClasses.primaryText}`}>
                    {message.sender === 'user' ? 'You' : message.llm_source || 'AI'} said:
                  </div>
                  <div className={`${themeClasses.secondaryText} whitespace-pre-wrap`}>
                    {message.text}
                  </div>
                  <div className={`text-xs mt-2 ${themeClasses.mutedText}`}>
                    {new Date(message.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={async () => {
              if (window.confirm('Are you sure you want to clear all chat history? This action cannot be undone.')) {
                try {
                  await localDbService.clearChatHistory(userId);
                  setChatHistory([]);
                  alert('Chat history cleared successfully.');
                } catch (err) {
                  console.error('Error clearing chat history:', err);
                  setError('Failed to clear chat history');
                }
              }
            }}
            className={`${themeClasses.buttonSecondary} text-red-600 px-4 py-2 rounded-lg hover:bg-red-50`}
          >
            Clear History
          </button>
          <button
            onClick={onClose}
            className={`${themeClasses.buttonPrimary} from py-2 rounded-lg`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}; 